import { useEffect, useRef, useState } from 'react';
import ImageEditor from './components/ImageEditor'
import { MaskEditorProvider } from 'react-canvas-masker';
import { useParams } from 'react-router-dom';
import { useEditImageMask, useGetSingleImage, useUndoEditImage } from './api';
import { Slider } from '@/shared/ui/slider.tsx';
import MaskEditorControls from './components/Editor/MaskEditorControls';
import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { SetStateAction } from 'jotai';

type Props = {
  setShowMask: React.Dispatch<SetStateAction<boolean>>
}


const MaskEditor = ({ setShowMask }: Props) => {

  const { imageId } = useParams()

  const { imageData } = useGetSingleImage(imageId)

  const undoRef = useRef(false)

  const [input, setInput] = useState('')

  const [mask, setMask] = useState('')

  const [image, setImage] = useState('');

  useEffect(() => {
    if (imageData) {
      setImage(imageData.image_url)
    }
  }, [imageData])

  const imageChangeHandlerOne = (url: string, canUndo: boolean) => {
    setImage(url)
    undoRef.current = canUndo
  }

  const imageChangeHandler = (id: number,url: string, canUndo?: boolean) => {
    console.log(id)
    setImage(url)
    undoRef.current = !!canUndo
  }

  const { editImageMask, status } = useEditImageMask(imageChangeHandlerOne)

  const { undoStatus, undoImage } = useUndoEditImage(imageChangeHandler)

  const imageUndoHandler = () => {
    if (imageData) {
      undoImage(imageData.image_id)
    }
  }

  const [brushSize, setBrushSize] = useState([40])

  const inputChangeHandler = (value: string) => {
    setInput(value)
  }


  return (
    <div className='w-full flex flex-col lg:flex-row bg-white bg-clip-padding backdrop-filter overflow-hidden backdrop-blur-xl bg-opacity-80 backdrop-saturate-100 shadow-[-1px_4px_12px_-3px_rgba(236,_72,_153,_0.5)] backdrop-contrast-150 sm:w-[calc(100%-2rem)] rounded-3xl h-full mx-auto space-y-3'>
      <section className='w-full lg:w-4/5 h-[1200px] lg:h-full p-4 relative' id="editor">
        <div
          className={cn(
            "absolute inset-0",
            "[background-size:30px_30px]",
            "[background-image:radial-gradient(#5512ff_1.2px,transparent_1px)]",
            "dark:[background-image:radial-gradient(#404040_1px,transparent_1px)]",
          )}
        />
        {/* Radial gradient for the container to give a faded look */}
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-white [mask-image:radial-gradient(ellipse_at_center,transparent_20%,black)] dark:bg-black"></div>

        {(status === "pending" || undoStatus === "pending") && (
          <div className='absolute inset-10 bg-white/50 z-[99] rounded-xl border border-white/80 flex flex-col gap-2 font-poppins font-semibold tracking-wide items-center justify-center'>
            <Loader2 className='animate-spin' />
            <p>Processing Image</p>
          </div>
        )}

        <MaskEditorProvider
          onDrawingChange={() => { }}
          onMaskChange={(img: string) => setMask(img)}
          src={image}
          maskOpacity={0.4}
          scale={1}
          minScale={0.2}
          maxScale={5}
          enableWheelZoom={true}
          constrainPan={true}
          cursorSize={brushSize[0]}
          maskBlendMode='screen'
        >
          <ImageEditor />
          <MaskEditorControls
            canUndo={undoRef.current}
            onImageUndo={imageUndoHandler}
            input={input} setInput={inputChangeHandler} handleSend={() => {
              if (input.length > 0 && mask.length > 0 && imageData) {
                console.log(input, mask)
                editImageMask({
                  image_id: imageData.image_id,
                  mask: mask,
                  prompt: input
                })
              }
            }} />
        </MaskEditorProvider>
      </section>
      <section className='w-full lg:w-1/5 bg-gradient-to-b from-white to-purple-50 !mt-0 border-s-indigo-300 border-s h-full p-2 flex flex-col gap-2' id="editor-siai">
        <h2 className='font-montserrat text-sm font-semibold'>Canvas Mode</h2>

        <p className='font-poppins font-medium text-sm pt-2'>Select brush size</p>
        <Slider
          max={80}
          step={1}
          className='text-purple-600'
          value={brushSize}
          onValueChange={(val: number[]) => {
            setBrushSize(val)
          }}
        />
        <p className='font-poppins font-medium text-sm pt-2'>Prompt options</p>
        <div className='w-full grid grid-cols-2 gap-1'>
          <p className='h-7 hover:bg-purple-200 transition-all ease-in-out duration-300 text-xs text-center w-full font-poppins p-1 border border-purple-300 rounded-md cursor-pointer'>Lorem Ipsum</p>
          <p className='h-7 hover:bg-purple-200 transition-all ease-in-out duration-300 text-xs text-center w-full font-poppins p-1 border border-purple-300 rounded-md cursor-pointer'>Lorem Ipsum</p>
          <p className='h-7 hover:bg-purple-200 transition-all ease-in-out duration-300 text-xs text-center w-full font-poppins p-1 border border-purple-300 rounded-md cursor-pointer'>Lorem Ipsum</p>
          <p className='h-7 hover:bg-purple-200 transition-all ease-in-out duration-300 text-xs text-center w-full font-poppins p-1 border border-purple-300 rounded-md cursor-pointer'>Lorem Ipsum</p>
        </div>
        <Button onClick={() => setShowMask(false)} variant="outline" className='rounded-lg text-sm font-poppins tracking-wide'>Add Text</Button>
      </section>
    </div>
  )
}

export default MaskEditor
